import React, { useEffect, useRef, useState } from "react";
import classnames from "classnames";
import styles from "./PullToRefresh.module.scss";

export interface PullToRefreshProps {
    /** Обновить данные; пока обещание не выполнено, показан индикатор. Ошибку обрабатывает вызывающий */
    onRefresh: () => Promise<unknown>;
    children?: React.ReactNode;
    className?: string;
}

/** Палец сдвигается больше, чем индикатор: тянуть приходится «с сопротивлением» */
const RESISTANCE = 0.5;
/** Насколько нужно вытянуть индикатор (px), чтобы отпускание запустило обновление */
export const PULL_THRESHOLD = 64;
/** Предел вытягивания индикатора, px */
const PULL_MAX = 96;
/** Высота индикатора во время обновления, px */
const HOLD = 48;

/** Ближайший прокручиваемый предок; прокрутки нет — страница */
function scrollParent(node: HTMLElement): HTMLElement | null {
    let parent = node.parentElement;

    while (parent) {
        const overflow = getComputedStyle(parent).overflowY;

        if (overflow === "auto" || overflow === "scroll") {
            return parent;
        }

        parent = parent.parentElement;
    }

    return null;
}

function atTop(node: HTMLElement): boolean {
    const parent = scrollParent(node);

    return (parent ? parent.scrollTop : window.scrollY) <= 0;
}

/**
 * «Потянуть вниз, чтобы обновить» (touch-события, без Blueprint): жест засчитывается, только если
 * прокрутка стоит в самом верху. Индикатор следует за пальцем, после отпускания дальше порога
 * остаётся на время `onRefresh`. Повторное обновление, пока идёт прежнее, не запускается.
 */
export const PullToRefresh = ({ onRefresh, children, className }: PullToRefreshProps) => {
    const root = useRef<HTMLDivElement>(null);
    const refresh = useRef(onRefresh);
    const [pull, setPull] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const busy = useRef(false);

    useEffect(() => {
        refresh.current = onRefresh;
    });

    useEffect(() => {
        const node = root.current;

        if (!node) {
            return undefined;
        }

        let startY: number | null = null;
        let current = 0;

        const reset = () => {
            startY = null;
            current = 0;
            setPull(0);
        };

        const onStart = (event: TouchEvent) => {
            startY = !busy.current && event.touches.length === 1 && atTop(node) ? event.touches[0].clientY : null;
            current = 0;
        };

        const onMove = (event: TouchEvent) => {
            if (startY === null) {
                return;
            }

            const delta = (event.touches[0].clientY - startY) * RESISTANCE;

            if (delta <= 0) {
                // палец пошёл вверх: это прокрутка
                if (current > 0) {
                    reset();
                }

                return;
            }

            current = Math.min(delta, PULL_MAX);
            setPull(current);

            // браузер не должен в это время сам прокручивать страницу или обновлять её целиком
            if (event.cancelable) {
                event.preventDefault();
            }
        };

        const onEnd = () => {
            if (startY === null) {
                return;
            }

            const trigger = current >= PULL_THRESHOLD;

            reset();

            if (!trigger || busy.current) {
                return;
            }

            busy.current = true;
            setRefreshing(true);

            let result: Promise<unknown>;

            try {
                result = refresh.current();
            } catch (er) {
                result = Promise.reject(er);
            }

            void result
                .catch(() => undefined)
                .finally(() => {
                    busy.current = false;
                    setRefreshing(false);
                });
        };

        node.addEventListener("touchstart", onStart, { passive: true });
        node.addEventListener("touchmove", onMove, { passive: false });
        node.addEventListener("touchend", onEnd);
        node.addEventListener("touchcancel", reset);

        return () => {
            node.removeEventListener("touchstart", onStart);
            node.removeEventListener("touchmove", onMove);
            node.removeEventListener("touchend", onEnd);
            node.removeEventListener("touchcancel", reset);
        };
    }, []);

    const height = refreshing ? HOLD : pull;
    const ready = pull >= PULL_THRESHOLD;

    return (
        <div ref={root} className={classnames(styles.root, className)}>
            <div
                className={classnames(styles.indicator, { [styles.animated]: pull === 0 })}
                style={{ height }}
            >
                {/* сообщение для скринридера есть, только пока идёт обновление: пустой role=status на странице не нужен */}
                {refreshing && (
                    <span className={styles.srOnly} role="status">
                        Обновление
                    </span>
                )}
                {(refreshing || pull > 0) && (
                    <span
                        className={classnames(styles.spinner, { [styles.spinning]: refreshing, [styles.ready]: ready })}
                        style={refreshing ? undefined : { transform: `rotate(${pull * 4}deg)` }}
                        aria-hidden="true"
                    />
                )}
            </div>
            {children}
        </div>
    );
};
